import { Card, CardHeader, Divider, Box, IconButton, Tooltip, Button, Typography, CircularProgress } from "@mui/material"
import { ListContextProvider, ReduxState, useDeleteWithConfirmController, useGetOne, useListContext, Record } from "ra-core"
import { createContext, Dispatch, FC, useCallback, useEffect, useMemo, useReducer } from "react"
import { Datagrid } from "@/components/list/datagrid"
import Scrollbar from "@/components/Scrollbar"
import { RecPatch } from "@/dataProvider/tools"
import { OdResourcePageProps } from "@/types/resource"
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { FunctionField, TextField } from "@/components/field"
import useLocalListController from "@/components/list/useLocalListController"
import CreateOrEdit from "@/controller/CreateOrEdit"
import EditObfuscationRule from "@/components/settings/encryption/EditObfuscationRule"
import { LastActivityKind, ObfuscationDataType, ObfuscationRuleDto, ObfuscationRuleStatus, ObfuscationRuleType, ObfuscationRuleUsageBriefDto } from "@/gen/api/encrypt"
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import EditIcon from "@mui/icons-material/Edit"
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import Confirm from "@/components/widgets/Confirm"
import { useResourceRegister } from "@/utils/resourceRegister"
import { useClickOnce } from "@/utils/useClickOnce"
import { useDispatch, useSelector } from "react-redux"
import { useSuppressAlertUponSave } from "@/pages/obfuscation/EncryptionComposite"
import { useLocation } from "react-router-dom"
import { push } from "connected-react-router"
import { AtopFuncArea, CustomFunctionField, FilterField, FilterOption, SearchField } from "@/components/list/datagridFunctionWidgets"
import { MANAGE_OBFUSCATION_RULES } from "@/constants"
import { useHasPermit } from "@/authProvider/useHasPermit"


const listOptions = {
  sorters: {
    ruleName: (a:ObfuscationRuleDto&RecPatch, b:ObfuscationRuleDto&RecPatch) => (a.ruleName || '') < (b.ruleName || '') ? -1 : 1,
  }
}

const briefInfoRes = 'settings-obfs-rule-usage-brief'

const ObfuscationRuleList: FC<OdResourcePageProps> = (props) => {
  useResourceRegister({ name: 'settings-select-databases' })
  useResourceRegister({ name: 'settings-select-tables' })
  useResourceRegister({ name: 'settings-select-fields' })
  useResourceRegister({ name: briefInfoRes })

  const { resource } = props
  const listCtrl = useLocalListController({ resource, basePath: '', filter: {}, sort: { field: 'ruleName', order: 'asc' } }, listOptions)
  
  return (
    <ListContextProvider value={listCtrl}>
      <ObfuscationRuleListBody {...props} />
    </ListContextProvider>
  )
}

const initialStates = {
  openEdit: false,
  recordToEdit: null as ObfuscationRuleDto|null,
  recordToDel: null as ObfuscationRuleDto|null,
  lastUpdateMode: 'Create' as 'Create'|'Edit',
  fromLocationToOpenId: null as number|null,
}
type PageAction =
| { type: 'closeEdit' }
| { type: 'openEdit', payload: ObfuscationRuleDto|null }
| { type: 'setToDel', payload: ObfuscationRuleDto|null }
| { type: 'openFromLocation', payload: number }

const reducer = (prevState: typeof initialStates, action: PageAction): typeof initialStates => {
  switch (action.type) {
    case 'closeEdit': {
      return { ...prevState, openEdit: false, recordToEdit: null }
    }
    case 'openEdit': {
      if (!!action.payload && action.payload === prevState.recordToEdit) { return prevState }
      return { ...prevState, openEdit: true, recordToEdit: action.payload || null, lastUpdateMode: action.payload ? 'Edit' : 'Create', fromLocationToOpenId: null }
    }
    case 'setToDel': {
      return { ...prevState, recordToDel: action.payload || null }
    }
    case 'openFromLocation': {
      return { ...prevState, fromLocationToOpenId: action.payload }
    }
  }
}

const ObfuscationRuleListBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_OBFUSCATION_RULES])
  const location = useLocation()
  const listCtx = useListContext<ObfuscationRuleDto & RecPatch>()
  const { refetch } = listCtx
  
  const [states, dispatch] = useReducer(reducer, initialStates)

  const closeEdit = useCallback(() => {
    dispatch({ type: 'closeEdit' })
  }, [dispatch])

  const savedHandlers = useDefaultSavedHandlers(() => `${states.lastUpdateMode} Obfuscation Rule`)
  savedHandlers.onSuccess.merge(refetch)
  savedHandlers.onSuccess.merge(closeEdit)

  const toDel = useSuppressAlertUponSave(((states.recordToDel || {}) as any) as Record, { databaseId: states.recordToDel?.databaseId as any, kind: LastActivityKind.ObfsRuleObsolete, actionArgs: [ `${states.recordToDel?.ruleId}`] })
  const delHandlers = useDefaultSavedHandlers('Delete Obfuscation Rule')
  delHandlers.onSuccess.merge(refetch)
  const {
    open: openConfirm,
    loading: deleting,
    handleDelete,
    handleDialogClose,
    handleDialogOpen,
  } = useDeleteWithConfirmController({ 
    record: (states.recordToDel?.ruleType === ObfuscationRuleType.TableColumn ? toDel : states.recordToDel) as any,
    onClick: () => {
      if (states.recordToDel?.ruleType !== ObfuscationRuleType.ClearData) { return }

    },
    ...delHandlers,
  })

  const dispatchEffect = useDispatch()
  useEffect(() => {
    if (location.state && (location.state as any).open) {
      const toOpenId: number = (location.state as any).open
      dispatch({ type: 'openFromLocation', payload: toOpenId })
      dispatchEffect(push({...location, state: null}))
    }
  }, [location.state, dispatchEffect])
  const clearDataRuleExists = useSelector<ReduxState, boolean>(store => {
    const allRules = store.admin.resources[resource || 'noop']
    const toOpenId = states.fromLocationToOpenId
    if (!allRules) { return false }

    if (toOpenId) {
      const toOpenRule = allRules.data[toOpenId]
      if (toOpenRule) {
        setTimeout(() => {
          dispatch({ type: 'openEdit', payload: toOpenRule as any })
        })
      }
    }
    return allRules.list.ids.some(id => allRules.data[id].ruleType === ObfuscationRuleType.ClearData)
  })

  const { data: briefInfo, loading } = useGetOne<ObfuscationRuleUsageBriefDto&RecPatch>(states.recordToDel ? briefInfoRes : 'noop', states.recordToDel?.ruleId || -1)

  const clickOnce = useClickOnce()
  return (
    <Card>
      <CardHeader 
        title="Obfuscation Rules" 
        action={
          hasWritePermit &&
          <Button
            color="primary"
            variant="contained"
            onClick={() => dispatch({ type: 'openEdit', payload: null })}
          >
            Add Rule
          </Button>
        }
      />
      <Divider />
      <AtopFuncArea>
        <SearchField 
          sourceKey="ruleName"
          match={(d: ObfuscationRuleDto, fval:string) => !!d.ruleName && d.ruleName.toLowerCase().includes(fval.trim().toLowerCase())}
          sx={{ width: 280 }}
          TextFieldProps={{ placeholder: 'Search by Rule Name' }}
        />
        <FilterField 
          label="Type" 
          filterOptions={typeOptions} 
          sourceKey="type"
          match={(d: ObfuscationRuleDto, fval:FilterOption) => fval.value === '_empty_' || d.ruleType === fval.value}
          sx={{ width: 190 }}
        />
        <FilterField 
          label="Status" 
          filterOptions={statusOptions} 
          sourceKey="status"
          match={(d: ObfuscationRuleDto, fval:FilterOption) => fval.value === '_empty_' || d.status === fval.value}
          sx={{ width: 190 }}
        />
        <CustomFunctionField 
          sourceKey="__"
          render={({setFilterVal, values}) => {
            const toOpenId = (location.state as any)?.open
            if (toOpenId && values['status'] === '_empty_') {
              setFilterVal('status', statusOptions[0])
            }
            return null
          }}
        />
      </AtopFuncArea>
      <Scrollbar options={{ suppressScrollY: true }}>
        <Box sx={{ minWidth: 1150 }}>
          <Datagrid 
            hasBulkActions={false} 
            sx={{ 
              '& th' : { py: 2 },
              '& td' : { py: 2 },
              '& .name': { maxWidth: 150, display: 'inline-block', wordBreak: 'break-word' },
              '& .description': { maxWidth: 500, display: 'inline-block', wordBreak: 'break-word' },
          }}
            {...listCtx}
          >
            <TextField source="ruleName" label="Name" className="name" />
            <FunctionField 
              label="Type" 
              sortable={false} 
              render={(d?:ObfuscationRuleDto&RecPatch) => (
                <>
                  <Typography variant="caption">
                    {d?.status === ObfuscationRuleStatus.Obsolete &&
                      <Typography variant="caption" color="warning.main">
                        Obsolete
                        <br/>
                      </Typography>
                    }
                    {d?.ruleType === ObfuscationRuleType.ClearData ? ObfuscationRuleType[ObfuscationRuleType.ClearData] :
                      `${ObfuscationDataType[(d?.dataType || ObfuscationDataType.String)]
                      } / ${ObfuscationRuleType[(d?.ruleType || ObfuscationRuleType.TableColumn)]}`
                    }
                  </Typography>
                </>
              )}
            />
            <TextField source="description" label="Description" className="description" sortable={false} />
            <TextField source="databaseName" label="Related Database" sortable={false} />
            <FunctionField
              label="Action"
              sortable={false}
              render={(d?:ObfuscationRuleDto&RecPatch) => (
                <>
                  <Tooltip title={hasWritePermit ? "Edit" : "View"}>
                    <IconButton
                      sx={{
                        color: 'text.primary' as any,
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                      onClick={() => dispatch({ type: 'openEdit', payload: d || null })}
                    >
                      {hasWritePermit ? <EditIcon fontSize="small" /> : <ArrowForwardIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  {hasWritePermit &&
                    <Tooltip title="Delete">
                      <IconButton
                        sx={{
                          color: 'text.primary' as any,
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onClick={(e:any) => {
                          dispatch({ type: 'setToDel', payload: d || null })
                          handleDialogOpen(e)
                        }}
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>}
                </>
              )}
            />
          </Datagrid>
        </Box>
      </Scrollbar>
      {//@ts-ignore 
      <DefaultPagination {...listCtx} />}
      <ObfuscationRuleListContext.Provider value={useMemo(() => ({dispatch, refetch}), [dispatch, refetch])}>
        <CreateOrEdit resource={resource} basePath="" record={states.recordToEdit} {...savedHandlers} >
          {states.openEdit && <EditObfuscationRule open={states.openEdit} onClose={closeEdit} clearDataRuleExists={clearDataRuleExists} />}
        </CreateOrEdit>
      </ObfuscationRuleListContext.Provider>
      {hasWritePermit &&
        <Confirm
          isOpen={openConfirm}
          title={"Delete Obfuscation Rule"}
          onConfirm={clickOnce(handleDelete)}
          onClose={(e:any) => {handleDialogClose(e); dispatch({ type: 'setToDel', payload: null }) }}
          loading={deleting}
          confirm="Confirm"
          cancel="Cancel"
          content={(
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 550 }}>
                {loading
                  ? <CircularProgress sx={{ mx: 'auto', mt: 2 }} />
                  : <Box>
                    {briefInfo && !!briefInfo.totalUsageCount &&
                      <>
                        <Typography
                          variant="body2"
                          sx={{ ml: 1 }}
                        >
                          The following fields are applied with this 
                          {states.recordToDel ? ` "${states.recordToDel?.ruleName}" ` : ' '} 
                          obfuscation rule. 
                          The obfuscation rule for these fields will be changed to None upon deletion. 
                        </Typography>
                        <Box 
                          sx={{ 
                            mt: 2,
                            borderRadius: '8px', 
                            px: 3,
                            py: 1,
                            bgcolor: theme => theme.palette.background.default 
                          }}
                        >
                          {briefInfo?.appliedFields?.map((t, i) => (
                            <Typography 
                              key={i}
                              component="p"
                              variant="caption"
                            >
                              {t.databaseName}. {t.schemaName}. {t.tableName}.
                              <b> {t.fieldName}</b>
                            </Typography>
                          ))}
                        </Box>
                        {(briefInfo?.totalUsageCount || 0) > (briefInfo?.appliedFields || []).length &&
                          <Typography
                            variant="caption"
                            sx={{ ml: 3 }}
                          >
                            And {(briefInfo?.totalUsageCount || 0) - (briefInfo?.appliedFields || []).length} more.
                          </Typography>
                        }
                      </>
                    }
                      <Typography
                        variant="body2"
                        sx={{ mt: 2 }}
                      >
                        Please confirm whether to proceed the deletion of this
                        {` "${states.recordToDel?.ruleName || ''}" `}
                        Obfuscation Rule.
                      </Typography>
                    </Box>
                }
              </Box>
            </Box>
          )}
        />}
    </Card>
  )
}

export default ObfuscationRuleList;

export const ObfuscationRuleListContext = createContext<{dispatch: Dispatch<PageAction>, refetch: ()=>void}>({
  dispatch: () => {},
  refetch: () => {},
})

const typeOptions = [
  { label: 'Clear Data', value: ObfuscationRuleType.ClearData },
  { label: 'Table Column', value: ObfuscationRuleType.TableColumn },
  { label: 'Value List', value: ObfuscationRuleType.ValueList },
  { label: 'Value Range', value: ObfuscationRuleType.ValueRange },
]

const statusOptions = [{ label: 'Obsolete', value: ObfuscationRuleStatus.Obsolete }]
